/**
 * The Automations list: what it reads and the switch on each row, shared by the computer's cards and the Season rows.
 */
import { useCallback, useEffect, useState } from "react";
import type { AutomationDetail, AutomationListResponse, AutomationStatus, AutomationSummary } from "../../shared/types";
import { useToast } from "../components/ui/Toast";
import { api } from "../lib/api";
import { treeToGraph } from "./flow-model";
import type { Start } from "./starts";
import { useOnShownAgain } from "../lib/inApp";
import { errorText } from "./words";

export function useAutomations() {
  const toast = useToast();
  const [data, setData] = useState<AutomationListResponse | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    api.automations
      .list()
      .then((d) => {
        setData(d);
        setFailed(false);
      })
      .catch(() => setFailed(true));
  }, []);
  useEffect(() => load(), [load]);
  useOnShownAgain(load);

  const switchOn = (a: AutomationSummary, on: boolean) => {
    const status: AutomationStatus = on ? "on" : "off";
    setData((d) => (d ? { ...d, automations: d.automations.map((x) => (x.id === a.id ? { ...x, status } : x)) } : d));
    toast({ text: on ? `${a.name} is on.` : `${a.name} is off.`, icon: on ? "check" : "pause" });
    return api.automations.update(a.id, { status }).then(() => undefined, (err) => {
      toast(errorText(err, "The switch did not save"));
      load();
    });
  };

  return { data, failed, load, switchOn };
}

/** Makes an automation from a ready-made start: its trigger, stop rule and steps. */
export async function createFromStart(start: Start): Promise<AutomationDetail> {
  const made = await api.automations.create({ name: start.name, trigger: start.trigger });
  if (start.stopWhen) await api.automations.update(made.id, { stop_when: start.stopWhen });
  const steps = start.steps();
  if (steps.length) await api.automations.saveGraph(made.id, { graph: treeToGraph(steps) });
  return made;
}
